import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Link } from 'react-router-dom';

import { renderBnplLayout } from '../components/Bnpl/BnplLayout';
import { Breadcrumb, BreadcrumbItem } from '../components/Breadcrumb';
import FileModal from '../components/FileModal';
import LoanBanner from '../components/LoanBanner';
import LoanFileItem from '../components/LoanFileItem';
import LoanStatementDetails from '../components/LoanStatementDetails';
import Layout from '../components/common/Layout';
import configData from '../config';
import { useLoanDetails } from '../hooks/useLoanDetails';
import { useLoanStatements } from '../hooks/useLoanStatements';
import { getApplications, getContract } from '../service/api';

function showFile(blob, fileName, format) {
  // It is necessary to create a new blob object with mime-type explicitly set
  // otherwise only Chrome works like it should
  const newBlob = new Blob([blob], { type: format });

  // IE doesn't allow using a blob object directly as link href
  // instead it is necessary to use msSaveOrOpenBlob
  if (window.navigator && window.navigator.msSaveOrOpenBlob) {
    window.navigator.msSaveOrOpenBlob(newBlob);
    return;
  }

  // For other browsers:
  // Create a link pointing to the ObjectURL containing the blob.
  const data = window.URL.createObjectURL(newBlob);
  var link = document.createElement('a');
  link.href = data;
  link.download = fileName;
  link.click();
  setTimeout(function () {
    // For Firefox it is necessary to delay revoking the ObjectURL
    window.URL.revokeObjectURL(data);
  }, 100);
}

const LoanDocumentsPage = () => {
  const [fileOpen, setFileOpen] = useState(false);
  const [fileUrl, setFileUrl] = useState('');
  const [currentFeature, setCurrentFeature] = useState({});
  const [selectedStatementId, setSelectedStatement] = useState(null);
  const [loadingApplications, setLoadingApplications] = useState(true);
  const { loan, loanLoading } = useLoanDetails();
  const { loanStatements, loanStatementsLoading } = useLoanStatements();
  const isBnpl = configData.getTenant() === 'lightrx';
  const allLoadingCompleted = !loadingApplications && !loanStatementsLoading && !loanLoading;
  useEffect(() => {
    const getInstances = async () => {
      const res = await getApplications();
      if (res && res.data && loan) {
        const loanInstance = res?.data?.data?.filter(
          (item) => item?.id === loan?.tenantLoanApplicationInstanceId
        )[0];
        let docFeature = loanInstance?.applicationFeatures?.find(
          (feature) => feature.itemType === 'ContractDocumentProperty'
        );
        if (!docFeature) {
          docFeature = loanInstance?.applicationFeatures?.find(
            (feature) => feature.name === 'contract'
          );
        }
        setCurrentFeature(docFeature);
        setLoadingApplications(false);
      }
    };
    getInstances();
  }, [loan, loan?.id, loan?.tenantLoanApplicationInstanceId]);

  const selectedStatement = useMemo(
    () => loanStatements && loanStatements.find((s) => s.statementRowId === selectedStatementId),
    [loanStatements, selectedStatementId]
  );

  const onDownload = useCallback(
    async (fileIndex) => {
      if (currentFeature?.value) {
        const fileName = currentFeature?.value?.signers[fileIndex]?.savedDocumentLocation;
        const { data } = await getContract(fileName);
        showFile(data, `Loan-document.pdf`, 'application/pdf');
      }
    },
    [currentFeature?.value]
  );

  const toggleFileOpen = async (fileIndex) => {
    if (!fileOpen) {
      setFileOpen(!fileOpen);
      const fileName = currentFeature?.value.signers[fileIndex]?.savedDocumentLocation;
      const { data } = await getContract(fileName);

      const file = new Blob([data], { type: 'application/pdf' });
      const fileURL = URL.createObjectURL(file);
      setFileUrl(fileURL);
      return;
    }
    setFileUrl('');
    setFileOpen(false);
  };

  const toggleFileOpenHtml = async (fileName) => {
    if (!fileOpen) {
      setFileOpen(!fileOpen);
      const { data } = await getContract(fileName);

      const file = new Blob([data], { type: 'text/html' });
      const fileURL = URL.createObjectURL(file);
      setFileUrl(fileURL);
      return;
    }
    setFileUrl('');
    setFileOpen(false);
  };

  const onDownloadHtml = useCallback(
    async (fileName) => {
      if (currentFeature?.value) {
        const { data } = await getContract(fileName);
        showFile(data, `Loan-document.html`, 'text/html');
      }
    },
    [currentFeature?.value]
  );

  const resetSelectedStatement = useCallback(() => setSelectedStatement(null), []);

  const renderDetailOrFiles = () => {
    return selectedStatement ? (
      <LoanStatementDetails
        data={selectedStatement}
        downloadStatement={onDownload}
        onBack={resetSelectedStatement}
      />
    ) : (
      <div className="amazonLoan__data wrapped">
        <div className="amazonLoan__data__section-files">
          <h2 className="amazonLoan__data__section-files-title">Documents</h2>
          {currentFeature?.value?.signers?.map(({ savedDocumentLocation }, index) => (
            <LoanFileItem
              key={savedDocumentLocation}
              name={'Signed Contract'}
              onDownload={() => onDownload(index)}
              toggleFileOpen={() => toggleFileOpen(index)}
            />
          ))}
          {currentFeature?.value?.length > 0 && currentFeature?.value[0]?.fileId && (
            <LoanFileItem
              key={currentFeature?.value[0]?.fileId}
              name={'Signed Contract'}
              onDownload={() => onDownloadHtml(currentFeature?.value[0]?.fileId)}
              toggleFileOpen={() => toggleFileOpenHtml(currentFeature?.value[0]?.fileId)}
            />
          )}
        </div>
      </div>
    );
  };

  if (isBnpl) {
    return renderBnplLayout(
      <Layout>
        <FileModal
          fileOpen={fileOpen}
          toggleFileOpen={toggleFileOpen}
          contract={<object data={fileUrl} width="100%" height="100%" />}
        />
        {allLoadingCompleted && (
          <div className="loanSection ant-layout">
            <div className="loanContainer">
              <div className="block breadcrumb">
                <Breadcrumb>
                  <BreadcrumbItem>
                    <Link to="/dashboard">Dashboard</Link>
                  </BreadcrumbItem>
                  {loan && <BreadcrumbItem>Loan #{loan?.loanDetail?.acctrefno}</BreadcrumbItem>}
                </Breadcrumb>
              </div>
              <LoanBanner data={loan?.loanDetail} />
              {renderDetailOrFiles()}
            </div>
          </div>
        )}
      </Layout>
    );
  }

  return (
    <Layout>
      <FileModal
        fileOpen={fileOpen}
        toggleFileOpen={toggleFileOpen}
        contract={<embed src={fileUrl} width="100%" height="90%" type="application/pdf" />}
      />
      {allLoadingCompleted && (
        <div className="loanSection ant-layout">
          <div className="loanContainer">
            <div className="block breadcrumb">
              <Breadcrumb>
                <BreadcrumbItem>
                  <Link to="/dashboard">Dashboard</Link>
                </BreadcrumbItem>
                {loan && <BreadcrumbItem>Loan #{loan?.loanDetail?.acctrefno}</BreadcrumbItem>}
              </Breadcrumb>
            </div>
            <LoanBanner data={loan?.loanDetail} />
            {renderDetailOrFiles()}
          </div>
        </div>
      )}
    </Layout>
  );
};

export default LoanDocumentsPage;
